import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { keepEdited } from "./fill";

/**
 * A form seeded from the server's data (S 2.7). `next` is the form as the data reads (null while it loads). Each time it changes – a refetch,
 * an agent's run, another officer's save – its values reach only the fields the person has not changed since the server's previous values
 * arrived; a field they wrote in keeps their text. `scope` names the record: moving to another record starts from its values.
 *
 *   const [study, setStudy] = useServerForm(report.data ? studyOf(report.data) : null, EMPTY_STUDY, caseId);
 */
export function useServerForm<T>(next: T | null | undefined, empty: T, scope?: unknown): [T, Dispatch<SetStateAction<T>>] {
  const [form, setForm] = useState<T>(() => next ?? empty);
  const last = useRef<T>(next ?? empty);
  const scoped = useRef(scope);
  const key = next == null ? null : JSON.stringify(next);
  useEffect(() => {
    if (next == null) return;
    const moved = scoped.current !== scope;
    scoped.current = scope;
    setForm((current) => (moved ? next : keepEdited(current, last.current, next)));
    last.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for `next` by its content, so an equal form read again changes nothing
  }, [key, scope]);
  return [form, setForm];
}
